import React, { useEffect, useState, useReducer, useCallback } from "react";
import PropTypes from 'prop-types';
import withSizes from 'react-sizes'
import Select from 'react-select';
import moment from 'moment-timezone';
import i18next from "i18next";
import { Map, TileLayer, Marker } from "react-leaflet";

import { AssetsService, DevicesService, TimeFormattingService } from "../../services";
import { GraphDataConverter } from '../../helpers';
import { DatePickerInput } from '../DatePickerInput';
import { LineGraph, DoorAreaGraph, StateAreaGraph } from '../Graphs';
import { DataTypeDisplayValue } from '../DataTypeDisplayValue';
import { DeviceDataOverrideService } from "../../services";

import config from "config";

const timeframeOptions = [
  {id: 0, label: 'form-labels.twelve-hours', timeframe: 12 * 60 * 60},
  {id: 1, label: 'form-labels.twenty-four-hours', timeframe: 24 * 60 * 60},
  {id: 2, label: 'form-labels.week', timeframe: 7 * 24 * 60 * 60}
];

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 993,
});

const convertDataTypesToMeasurementOptions = (dataTypes, runForDate, timeframe) => {
  return dataTypes
    .filter(({datatype}) => {
      if (datatype.startTime == null && datatype.endTime == null) {
        return true;
      } else {
        const runForStart = moment(runForDate).valueOf();
        const runForEnd = moment(runForDate).valueOf() - (timeframe * 1000);
        const dtStartTime = datatype.startTime;
        const dtEndTime = datatype.endTime == null ? 9999999999999 : datatype.endTime;
        if (runForStart >= dtStartTime && runForStart <= dtEndTime) {
          return true;
        }
        if (runForEnd >= dtStartTime && runForEnd <= dtEndTime) {
          return true;
        }
        if (dtStartTime <= runForStart && dtEndTime >= runForEnd) {
          return true;
        }
        return false;
      }
    })
    .sort((a, b) => {return a.datatype.displayOrder - b.datatype.displayOrder})
    .map(data => ({
      label: data.datatype.name, 
      value: data.datatype, 
      currentMeasurement: data?.currentMeasurement, 
      previousMeasurements: data?.currentMeasurement.measurementGroupId ? dataTypes.filter(({currentMeasurement}) => currentMeasurement.measurementGroupId === data?.currentMeasurement.measurementGroupId && currentMeasurement.endTime != null) : [],
      reading: data.reading
    }));
};

const DeviceViewDetailsGraph = ({ isMobile, entityTypeToken, entityId, dataType, dataTypes, singleSelectedMeasurement, timezone }) => {
  const runForDate = timezone ? new Date(moment().tz(timezone).valueOf()) : new Date(moment().valueOf());
  const measurementOptions = dataTypes && dataTypes.length > 0 ? convertDataTypesToMeasurementOptions(dataTypes, runForDate, timeframeOptions[0].timeframe) : [];
  const sortedDataTypes = dataTypes && dataTypes.length > 0 ? dataTypes.sort((a, b) => {return a.datatype.displayOrder - b.datatype.displayOrder}) : [];

  const [graphStateConfiguration, dispatch] = useReducer(graphStateConfigurationReducer, {
      entityId: entityId,
      entityTypeToken: entityTypeToken,
      timezone: timezone,
      needsMeasureUpdate: false,
      runForDate: runForDate,
      selectedDataType: sortedDataTypes.length > 0 ? sortedDataTypes[0].datatype : dataType,
      singleSelectedMeasurement: singleSelectedMeasurement,
      measurementOptions: measurementOptions,
      selectedMeasurementOption: measurementOptions.length > 0 ? measurementOptions[0] : null,
      filterTimeframeValue: { value: timeframeOptions[0].id, label: i18next.t(timeframeOptions[0].label) },
      isMobile: isMobile,
  });
  const [graphData, setGraphData] = useState(null);
  const [lastMeasurement, setLastMeasurement] = useState(null);

  useEffect(() => {
    setGraphData(null);

    const lastReceivedData = graphStateConfiguration.selectedMeasurementOption?.currentMeasurement?.lastReceivedData;
    if (lastReceivedData?.numericValue) {
      const fnToApply = DeviceDataOverrideService.getOverrideFunctionForDeviceTypeToken(lastReceivedData.token);
      setLastMeasurement({ ...lastReceivedData, numericValue: fnToApply(lastReceivedData.numericValue) });
    } else {
      setLastMeasurement(lastReceivedData);
    }

    if (graphStateConfiguration.needsMeasureUpdate && graphStateConfiguration.entityTypeToken == 'asset') {
        graphStateConfiguration.needsMeasureUpdate = false;
        graphStateConfiguration.measurementOptions = sortedDataTypes.length > 0 ? convertDataTypesToMeasurementOptions(sortedDataTypes, graphStateConfiguration.runForDate, timeframeOptions[0].timeframe) : [];
        graphStateConfiguration.selectedMeasurementOption = graphStateConfiguration.measurementOptions.length > 0 ? graphStateConfiguration.measurementOptions[0] : null;
    }

    const selectedDeviceDataType = graphStateConfiguration.selectedMeasurementOption ? graphStateConfiguration.selectedMeasurementOption.value : null;
    if (selectedDeviceDataType && (selectedDeviceDataType.token == "geo_lat_lon" || (selectedDeviceDataType.deviceDataClassification && selectedDeviceDataType.deviceDataClassification.token == "geo_position"))) {
      const complexValue = graphStateConfiguration.selectedMeasurementOption?.currentMeasurement?.lastReceivedData?.complexValue;
      if (complexValue?.latitude && complexValue?.longitude) {
          setGraphData({ geoData: complexValue });
      } else {
          setGraphData({ error: "No current value." });
      }
    } else {
      fetchUpdatedGraphStateForConfiguration(graphStateConfiguration)
              .then(setGraphData)
              .catch(error => { setGraphData({error: error}); });
    }

  }, [graphStateConfiguration]);

  const updateSelectedMeasurement = useCallback((selectedValue) => {
    dispatch({ type: "measure_changed", selectedValue: selectedValue });
  });

  const updateFilterTimeframeValue = useCallback((selectedValue) => {
    dispatch({ type: "timeframe_changed", selectedValue: selectedValue });
  });

  const handleDateChange = useCallback((value, formattedValue) => {
    dispatch({ type: "date_changed", value: value, formattedValue: formattedValue });
  });

  const refreshFunction = useCallback(() => {
    dispatch({ type: "refresh" });
  });

  const isLoading = graphData == null;
  const isError = graphData != null && graphData.error != null;

  return (
      <div style={{ minHeight: 510 }}>
          <DataConfigurationRow
              graphStateConfiguration={graphStateConfiguration}
              handleDateChange={handleDateChange}
              updateFilterTimeframeValue={updateFilterTimeframeValue}
              updateSelectedMeasurement={updateSelectedMeasurement}
          />

          {isLoading && <div className="loader">{i18next.t("loading")}</div>}

          {
            !isLoading && !isError && 
            <DataDisplayRow 
              isMobile={isMobile} 
              refreshFunction={refreshFunction} 
              lastMeasurement={lastMeasurement} 
              graphData={graphData} 
            />
          }

          {!isLoading && isError && <div style={{ paddingTop: 10 }}>{graphData.error}</div>}
      </div>
  );
};

DeviceViewDetailsGraph.propTypes = {
  entityTypeToken: PropTypes.string.isRequired,
  entityId: PropTypes.number.isRequired,
  dataType: PropTypes.object,
  dataTypes: PropTypes.array,
  timezone: PropTypes.string
};

const DataConfigurationRow = ({ graphStateConfiguration, handleDateChange, updateFilterTimeframeValue, updateSelectedMeasurement }) => {
  return (
    <div className="row">
      <DatePickerArea runForDate={graphStateConfiguration.runForDate} handleDateChange={handleDateChange} />

      <TimeframeSelectionArea filterTimeframeValue={graphStateConfiguration.filterTimeframeValue} updateFilterTimeframeValue={updateFilterTimeframeValue} />

      <MeasurermentSelectionArea 
          measurementOptions={graphStateConfiguration.measurementOptions} 
          selectedMeasurementOption={graphStateConfiguration.selectedMeasurementOption} 
          updateSelectedMeasurement={updateSelectedMeasurement}
      />
    </div>
  );
};

DataConfigurationRow.propTypes = {
  graphStateConfiguration: PropTypes.object.isRequired,
  handleDateChange: PropTypes.func.isRequired,
  updateFilterTimeframeValue: PropTypes.func.isRequired,
  updateSelectedMeasurement: PropTypes.func.isRequired
};

const DatePickerArea = ({ runForDate, handleDateChange }) => {
  return (
    <div className="col-sm-3">
        <label>{ i18next.t('form-labels.date') }</label>
        <DatePickerInput
          input={{value: runForDate}}
          meta={{}}
          onDayChange={handleDateChange}
        />
    </div>
  )
};

DatePickerArea.propTypes = {
  runForDate: PropTypes.object.isRequired,
  handleDateChange: PropTypes.func.isRequired
};

const TimeframeSelectionArea = ({ filterTimeframeValue, updateFilterTimeframeValue }) => {
  return (
    <div className="col-sm-3">
      <label>{ i18next.t('form-labels.timeframe') }</label>
      <Select
          className="react-select-container"
          classNamePrefix="react-select"
          options={timeframeOptions.map(option => ({value: option.id, label: i18next.t(option.label)}))}
          clearable={false}
          value={filterTimeframeValue}
          onChange={updateFilterTimeframeValue}
          searchable={false}
      />
    </div>
  );
};

TimeframeSelectionArea.propTypes = {
  filterTimeframeValue: PropTypes.object.isRequired,
  updateFilterTimeframeValue: PropTypes.func.isRequired
};

const MeasurermentSelectionArea = ({ measurementOptions, selectedMeasurementOption, updateSelectedMeasurement }) => {
  return (measurementOptions && measurementOptions.length > 0 && 
      <div className="col-sm-6">
        <label>{ i18next.t('form-labels.measurement') }</label>
        <Select
            className="react-select-container"
            classNamePrefix="react-select"
            options={measurementOptions}
            clearable={false}
            value={selectedMeasurementOption}
            onChange={updateSelectedMeasurement}
            searchable={false}
        />
      </div>
  );
};

MeasurermentSelectionArea.propTypes = {
  measurementOptions: PropTypes.array.isRequired,
  selectedMeasurementOption: PropTypes.object,
  updateSelectedMeasurement: PropTypes.func.isRequired
};

const GraphForDataType = ({ graphVitals, dataTypeToken }) => {
  if (dataTypeToken === 'magnetometer' || dataTypeToken === 'door_open') {
    return <DoorAreaGraph graphVitals={graphVitals} showBrush={!graphVitals.isMobile} width={graphVitals.isMobile ? 350 : 600} height={350} id={0} />;
  }

  if (graphVitals.data && graphVitals.data.length > 0 && graphVitals.data.filter(entry => entry.state != null).length > 0) {
    return <StateAreaGraph dataTypeToken={dataTypeToken} graphVitals={graphVitals} showBrush={!graphVitals.isMobile} width={graphVitals.isMobile ? 350 : 600} height={350} id={0} />;
  }

  // TODO need the default to be a no graph graph

  return <LineGraph graphVitals={graphVitals} showBrush={!graphVitals.isMobile} width={graphVitals.isMobile ? 350 : 600} height={350} />;
};

GraphForDataType.propTypes = {
    graphVitals: PropTypes.object.isRequired,
    dataTypeToken: PropTypes.string.isRequired
};

const DataAuxDisplayDataRow = ({ isMobile, refreshFunction, graphLastUpdated, lastMeasurement, range, selectedDataType }) => {
  return (
    <div className='row'>
      <div className="col-sm-10">
        {graphLastUpdated && <div className='row' style={{padding:10}}>
          <div className="col-sm-12">
            <strong><small>Data as of</small></strong> <em><small>{graphLastUpdated}</small></em>
          </div>
        </div>}

        <div className='row' style={{paddingLeft:10, paddingRight:10, paddingBottom:10}}>
          {
            lastMeasurement != null &&
            <div className="col-sm-5">
              <strong><small>Now</small></strong> 
              <em>
                <small> <br/>
                  <img width={"16"} height={"16"} style={{ marginRight: "3px" }} src={`${config.WEB_BASE_URL}/images/icon/${selectedDataType?.token}_reading.svg`} />
                  <DataTypeDisplayValue value={lastMeasurement} />
                </small>
              </em>
            </div>
          }

          { 
            range?.min != null && 
            <div className="col-sm-3">
              <strong><small>Min</small></strong> 
              <em>
                <small> <br/>
                  <DataTypeDisplayValue value={{ numericValue: range.min, deviceDataType: selectedDataType, displayFormat: selectedDataType?.deviceDataDisplayType?.format }} />
                </small>
              </em>
            </div> 
          }

          { 
            range?.max != null && 
            <div className="col-sm-3">
              <strong><small>Max</small></strong> 
              <em>
                <small> <br/>
                  <DataTypeDisplayValue value={{ numericValue: range.max, deviceDataType: selectedDataType, displayFormat: selectedDataType?.deviceDataDisplayType?.format }} />
                </small>
              </em>
            </div> 
          }
        </div>
      </div>

      {
        !isMobile && 
        <div className="col-sm-2">
          <div className="row">
            <div className="col-sm-12">
              <label style={{visibility: 'hidden'}}>{ i18next.t('refresh') }</label>
              <img className="refresh" src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/line_refresh.svg`} alt={ i18next.t('area') } onClick={refreshFunction} style={{height: '30px', marginLeft: '15px'}} />
            </div>
          </div>
        </div>
      }
    </div>
  );
};

DataAuxDisplayDataRow.propTypes = {
    isMobile: PropTypes.bool,
    refreshFunction: PropTypes.func,
    graphLastUpdated: PropTypes.string,
    lastValue: PropTypes.object,
    range: PropTypes.object,
    selectedDataType: PropTypes.object,
    deviceDataDisplayType: PropTypes.object
};

const DataDisplayRow = ({ isMobile, graphData, lastMeasurement, refreshFunction }) => {
  return (
    <>
      <DataAuxDisplayDataRow
        isMobile={isMobile}
        refreshFunction={refreshFunction}
        graphLastUpdated={graphData.graphLastUpdated}
        range={graphData.range}
        selectedDataType={graphData.deviceDataType}
        lastMeasurement={lastMeasurement}
      />

      {
        graphData.geoData ? 
          <GeoValueMap geoValue={graphData.geoData} isMobile={isMobile} />
          :
          <div className="chart-data text-center" style={{ height: "auto" }}>
            <GraphForDataType graphVitals={graphData.graphVitals} dataTypeToken={graphData.deviceDataType.token} />
          </div>
      }
    </>
  );
};

DataDisplayRow.propTypes = {
    isMobile: PropTypes.bool.isRequired,
    graphData: PropTypes.object.isRequired,
    refreshFunction: PropTypes.func.isRequired
};

const fetchUpdatedGraphStateForConfiguration = (graphStateConfiguration) => {
  let requestedEndAtDate = graphStateConfiguration.timezone ? moment().tz(graphStateConfiguration.timezone).valueOf() : moment().valueOf();  

  if (graphStateConfiguration.runForDate != null) {
    var dateValue = null;
    if (moment(graphStateConfiguration.runForDate).dayOfYear() === moment().dayOfYear() && moment(graphStateConfiguration.runForDate).year() === moment().year()) {
      dateValue = moment();
    } else {
      dateValue = moment(graphStateConfiguration.runForDate).endOf("day");
    }
    requestedEndAtDate = graphStateConfiguration.timezone ? moment(dateValue).tz(graphStateConfiguration.timezone).valueOf() : moment(dateValue).valueOf();
  }

  return new Promise((resolve, reject) => {
    if (graphStateConfiguration.entityTypeToken == 'asset') {
      
      const currentMeasurement = (graphStateConfiguration.singleSelectedMeasurement?.device && graphStateConfiguration.singleSelectedMeasurement?.deviceDataType) ? ({
        originatingDeviceSerialNumber: graphStateConfiguration.singleSelectedMeasurement?.device.serialNumber,
        originatingDeviceDataTypeToken: graphStateConfiguration.singleSelectedMeasurement?.deviceDataType.token
      }) : (graphStateConfiguration.singleSelectedMeasurement ? 
                                      graphStateConfiguration.singleSelectedMeasurement : 
                                      graphStateConfiguration.selectedMeasurementOption?.currentMeasurement);

      if (currentMeasurement) {
        var previousMeasurementsToFetch = [];
        if (graphStateConfiguration.selectedMeasurementOption?.previousMeasurements) {
          previousMeasurementsToFetch = graphStateConfiguration.selectedMeasurementOption?.previousMeasurements.map((pm) => pm.currentMeasurement);
        }
        
        if (previousMeasurementsToFetch.length > 0) {
          let requests = previousMeasurementsToFetch.map((aMeasurement) =>
            AssetsService.getAssetReceivedDataByAssetMeasure(graphStateConfiguration.filterTimeframeValue.value, aMeasurement.endTime, graphStateConfiguration.entityId, aMeasurement.originatingDeviceSerialNumber, aMeasurement.originatingDeviceDataTypeToken).then((response) => {
              return {
                request: aMeasurement,
                response: response
              };
            })
          );
          requests.push(
            AssetsService.getAssetReceivedDataByAssetMeasure(graphStateConfiguration.filterTimeframeValue.value, requestedEndAtDate, graphStateConfiguration.entityId, currentMeasurement.originatingDeviceSerialNumber, currentMeasurement.originatingDeviceDataTypeToken).then((response) => {
              return {
                request: currentMeasurement,
                response: response
              };
            })
          );

          Promise.all(requests).then((responses) => {
            const timezone = responses[0].response.timezone;
            const readings = responses.flatMap(({request, response}) => 
              response.readingsData.readings.filter((reading) => reading.timestamp > request.startTime && (reading.timestamp < request.endTime || request.endTime == null))
            ).sort((a, b) => a.timestamp - b.timestamp);

            if (readings.length > 0) {
              const firstReading = readings[0];
              const lastReading = readings[readings.length-1];

              const earliestDevice = responses.reduce((a, b) => (a.request.startTime < b.request.startTime) ? a : b);
              const lastDevice = responses.find(a => a.request.endTime == null);

              let outagesAtStartOrEnd = [];
              if (earliestDevice.response.readingsData.outagesAtStartOrEnd && earliestDevice.response.readingsData.outagesAtStartOrEnd.length > 0) {
                earliestDevice.response.readingsData.outagesAtStartOrEnd.forEach(outage => outagesAtStartOrEnd.push(outage));
              }
              if (lastDevice.response.readingsData.outagesAtStartOrEnd && lastDevice.response.readingsData.outagesAtStartOrEnd.length > 0) {
                lastDevice.response.readingsData.outagesAtStartOrEnd.forEach(outage => outagesAtStartOrEnd.push(outage));
              }
              if (firstReading != null && lastReading != null) {
                outagesAtStartOrEnd = outagesAtStartOrEnd.filter((outage) => outage.end <= firstReading.timestamp || outage.start >= lastReading.timestamp);
              }
              const outages = responses.flatMap(({response}) => response.readingsData.outages).filter(outage => outage != null);

              const requestedStartAtDate = requestedEndAtDate - timeframeOptions[graphStateConfiguration.filterTimeframeValue.value].timeframe * 1000;
              if (firstReading.timestamp > requestedStartAtDate) {
                readings.unshift({amount: null, state: null, genericString: null, deviceName: null, timestamp: requestedStartAtDate + 1000});
              }

              respondWithExtractedData(graphStateConfiguration, requestedEndAtDate, timezone, readings, outagesAtStartOrEnd, outages, resolve, reject);
            } else {
              reject(i18next.t("messages.no-readings-error"));
            }
          }).catch(error => {
              console.error(error)
              reject('Error loading graph data.');
          }); 
        } else {
          AssetsService.getAssetReceivedDataByAssetMeasure(graphStateConfiguration.filterTimeframeValue.value, requestedEndAtDate, graphStateConfiguration.entityId, currentMeasurement.originatingDeviceSerialNumber, currentMeasurement.originatingDeviceDataTypeToken).then(response => {
            respondWithExtractedData(graphStateConfiguration, requestedEndAtDate, response.readingsData.timezone, response.readingsData.readings, response.readingsData.outagesAtStartOrEnd, response.readingsData.outages, resolve, reject);
          }).catch(error => {
              console.error(error)
              reject('Error loading graph data.');
          });     
        } 
      } else {
        reject(i18next.t("messages.no-readings-error"));
      }
    } else {
      DevicesService.getDeviceReceivedDataByDeviceDataType(graphStateConfiguration.filterTimeframeValue.value, requestedEndAtDate, graphStateConfiguration.entityId, graphStateConfiguration.selectedDataType.id).then(response => {
        respondWithExtractedData(graphStateConfiguration, requestedEndAtDate, response.readingsData.timezone, response.readingsData.readings, response.readingsData.outagesAtStartOrEnd, response.readingsData.outages, resolve, reject);
      }).catch(error => {
          console.error(error)
          reject('Error loading graph data.');
      });
    }
  });
};

const respondWithExtractedData = (graphStateConfiguration, requestedEndAtDate, timezone, readings, inputOutagesAtStartOrEnd, outages, resolve, reject) => {
  if (readings.length < 1) {
    reject(i18next.t("messages.no-readings-error"));
  } else {
    const timerangeReadings = readings
            .filter(reading => {
              const isReadingBeforeRequestedEndAtDate = reading.timestamp < requestedEndAtDate;
              const isReadingAfterRequestedStartAtDate = reading.timestamp > requestedEndAtDate - timeframeOptions[graphStateConfiguration.filterTimeframeValue.value].timeframe * 1000;

              return isReadingBeforeRequestedEndAtDate && isReadingAfterRequestedStartAtDate;
            });

    if (timerangeReadings.length < 1) {
      reject(i18next.t("messages.no-readings-error"));
    } else {
      const outagesAtStartOrEnd = GraphDataConverter.convertOutagesToGraphData(inputOutagesAtStartOrEnd, timezone);

      const graphData = GraphDataConverter.convertReadingDataToGraphData(
        graphStateConfiguration.selectedDataType.token,
        timezone,
        timerangeReadings,
        requestedEndAtDate,
        timeframeOptions[graphStateConfiguration.filterTimeframeValue.value].timeframe
      );

      resolve({ 
        graphLastUpdated: TimeFormattingService.renderNow(graphStateConfiguration.timezone), 
        range: extractRangeValues(timerangeReadings, graphStateConfiguration.selectedDataType.token), 
        deviceDataType: graphStateConfiguration.selectedDataType,
        graphVitals: {
          data: graphData, 
          label: i18next.t([`device-data-display-types.${DeviceDataOverrideService.getOverrideForDisplayToken(graphStateConfiguration.selectedDataType.token)}`, `device-data-types.${graphStateConfiguration.selectedDataType.token}`]), 
          outages: outages,
          isMobile: graphStateConfiguration.isMobile, 
          outagesAtStartOrEnd: outagesAtStartOrEnd
        }
      });
    }
  }
};

const extractRangeValues = (timerangeReadings, deviceDataTypeToken) => {
    const fnToApply = DeviceDataOverrideService.getOverrideFunctionForDeviceTypeToken(deviceDataTypeToken);

    let rangeMin = null;
    let rangeMax = null;
    timerangeReadings.forEach(reading => {
      if (reading.amount != null && (rangeMax == null || reading.amount > rangeMax)) {
        rangeMax = reading.amount;
      }
      if (reading.amount != null && (rangeMin == null || reading.amount < rangeMin)) {
        rangeMin = reading.amount;
      }
    });
    
    return { min: fnToApply(rangeMin), max: fnToApply(rangeMax) };
};

const graphStateConfigurationReducer = (graphStateConfiguration, action) => {
  switch(action.type) {
    case 'date_changed': {
      return {
        ...graphStateConfiguration,
        needsMeasureUpdate: true,
        runForDate: action.value,
      };
    }
    case 'measure_changed': {
      return {
        ...graphStateConfiguration,
        selectedMeasurementOption: action.selectedValue,
        selectedDataType: action.selectedValue.value
      };
    }
    case 'timeframe_changed': {
      return {
        ...graphStateConfiguration,
        needsMeasureUpdate: true,
        filterTimeframeValue: action.selectedValue
      };
    }
    case 'refresh': {
      return {
          ...graphStateConfiguration
      };
    }
    default: {
      console.error(`Unknown action type: ${action.type}`);
      return graphStateConfiguration;
    }
  }
}

const GeoValueMap = ({ geoValue, isMobile }) => {
    if (geoValue == null) {
        return (<div>Location unavailable</div>);
    } else {
        return (
            <Map id="mapId" center={[geoValue.latitude, geoValue.longitude]} zoom={12} style={{marginTop: 10, height: 350, width: (isMobile? 350 : 600), zIndex: 0}}>
                <TileLayer attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors' url="http://{s}.tile.osm.org/{z}/{x}/{y}.png" />
                <Marker position={[geoValue.latitude, geoValue.longitude]} />
            </Map>
        );
    }
};

GeoValueMap.propTypes = {
    geoValue: PropTypes.object.isRequired,
    isMobile: PropTypes.bool.isRequired,
};

export default withSizes(mapSizesToProps)(DeviceViewDetailsGraph);
